import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icona } from '@/components/Icona';
import { seccioDeRuta } from '@/navigation/navigation';
import { useRealtimeStatus } from '@/realtime/RealtimeContext';
import { PestanyesSeccio } from './PestanyesSeccio';

const PUNT_ESTAT = {
  connectat: 'dot--green',
  connectant: 'dot--orange',
  desconnectat: 'dot--gray',
} as const;

/**
 * Capçalera de cada pantalla: enllaç de retorn opcional, títol gran amb insígnies, subtítol,
 * accions alineades a la dreta i, a sota, les pestanyes de la secció on és la pantalla.
 * Les pantalles d'Ajustos mostren sempre «← Ajustos» per tornar a l'índex.
 */
export function Toolbar({
  titol,
  subtitol,
  enrere,
  torna,
  badges,
  children,
  senseTabs,
}: {
  titol: string;
  subtitol?: ReactNode;
  /** Ruta de retorn: mostra el xebró a l'esquerra del títol. */
  enrere?: string;
  /** Enllaç de text sobre el títol («← Activacions»). */
  torna?: { ruta: string; text: string };
  badges?: ReactNode;
  children?: ReactNode;
  /** No mostra les pestanyes de secció (p. ex. assistents a pantalla completa). */
  senseTabs?: boolean;
}) {
  const { t } = useTranslation('comu');
  const estat = useRealtimeStatus();
  const { pathname } = useLocation();
  const seccio = seccioDeRuta(pathname);
  const tornaAjustos =
    !torna && !enrere && seccio === 'ajustos' && pathname !== '/admin'
      ? { ruta: '/admin', text: t('nav.seccions.ajustos') }
      : null;
  const enllacTorna = torna ?? tornaAjustos;
  return (
    <header className="toolbar">
      {enllacTorna ? (
        <Link className="toolbar__seccio" to={enllacTorna.ruta}>
          {t('nav.torna', { seccio: enllacTorna.text })}
        </Link>
      ) : null}
      <div className="toolbar__fila">
        {enrere ? (
          <Link className="toolbar__back" to={enrere} aria-label={t('accions.enrere')}>
            <Icona nom="chevron-left" />
          </Link>
        ) : null}
        <div className="toolbar__titol">
          <h1 title={titol}>{titol}</h1>
          {subtitol ? <span className="toolbar__subtitol">{subtitol}</span> : null}
        </div>
        {badges ? <div className="toolbar__badges">{badges}</div> : null}
        <div className="toolbar__spacer" />
        {children ? <div className="toolbar__accions">{children}</div> : null}
        <span className="toolbar__estat" data-estat={estat} title={t(`tempsReal.${estat}`)}>
          <i className={`dot ${PUNT_ESTAT[estat]}`} />
          <span className="sr-only">{t(`tempsReal.${estat}`)}</span>
        </span>
      </div>
      {seccio && !senseTabs ? <PestanyesSeccio seccio={seccio} /> : null}
    </header>
  );
}
